import type { ReactNode } from "react";
import type { PickupLocation } from "../../../api/pickupLocations";

// Harvest inventory (stored in MongoDB, exposed via /api/harvest-products)
export interface HarvestProduct {
  id: string;
  name: string;
  quantity: number;
  price: number;
  discount: number | null;
  createdAt: string;
  updatedAt: string;
}

// Shared button config for modals
export type ModalButton = {
  text: string;
  /** Shown while callback is running (API calls). */
  loadingText?: string;
  callback: () => void | Promise<void>;
};

// Info pages for help/guides
export type InfoPage = {
  title: string;
  text: string[];
};

//Modal
export type ModalConfig = {
  title?: ReactNode;
  body?: ReactNode;
  buttons?: ModalButton[];
  showCloseButton?: boolean;
};

// Add & Edit pickup locations
export type MapLocation = {
  address: string;
  latitude: number;
  longitude: number;
};

export type DayOption = {
  day: string;
  startTime: string;
  endTime: string;
};

export type ClientPickupFormData = {
  selectedPickupLocation: PickupLocation | undefined;
  selectedDay: string;
  friendPickupChecked: boolean;
  friendFirstName: string | null | undefined;
  friendLastName: string | null | undefined;
  friendPhone: string | null | undefined;
};

type FriendPickup = {
  firstName: string;
  surname: string;
  phoneNumber: string;
};
export enum PaymentStatus {
  Pending = "pending",
  Paid = "paid",
  Failed = "failed",
  Expired = "expired",
}
export const PAYMENT_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "failed", label: "Failed" },
  { value: "expired", label: "Expired" },
];

/** Admin create-client / add-CSA — failed & expired are set by the system only. */
export const ADMIN_ASSIGNABLE_PAYMENT_STATUS_OPTIONS = PAYMENT_STATUS_OPTIONS.filter(
  (option) =>
    option.value === PaymentStatus.Pending || option.value === PaymentStatus.Paid,
);
export type Delivery = {
  id: string;
  subscriptionPaymentStatus: PaymentStatus;
  firstName: string;
  surname: string;
  phoneNumber: string;
  email: string;
  pickupLocation: string;
  day: string;
  subscriptionTierId: string;
  friendPickup?: FriendPickup;
};

export type CartItem = {
  productName: string;
  unitPrice: number;
  quantity: number;
  totalPrice: number;
};

export type SeasonCheckboxes = {
  printHarvestLogs: boolean;
  printOrders: boolean;
  updatePickupLocations: boolean;
  updateSubscriptionsAndTiers: boolean;
  updateCsaDeliveryPrice: boolean;
};

export enum DeliveryMethod {
  Pickup = "pickup",
  Shipping = "shipping",
}

// ACCOUNTS

export enum AccountRole {
  Admin = "admin",
  Employee = "employee",
  Business = "business",
  SuperUser = "superuser",
  Social = "social",
}

export type BaseAccount = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber: string;
  role: AccountRole;
};
export type AdminAccount = BaseAccount;

export type EmployeeAccount = BaseAccount;

export type ClientAccount = BaseAccount & {
  subscriptionTierId: string;
};

export type SocialAccount = BaseAccount & {
  role: AccountRole.Social;
};

export type BusinessAccount = {
  companyName: string;
  companyRegistrationNumber: string;
  email: string;
  password: string;
  phoneNumber: string;
  address: string;
  postalCode: string;
  city: string;
  role: AccountRole.Business;
  shippingHeavyPrice?: number;
  shippingLightPrice?: number;
};

//HARVESTLOGS

export type HarvestLog = {
  logId: string;
  startDate: string;
  endDate: string;
  comment: string;
  products: LogProduct[];
};

type LogProduct = {
  product: string;
  quantity: number;
};
